import React from 'react';

/**
 * MaroonAmbientBackdrop — animated decorative backdrop used behind hero & key
 * sections to give a deep, premium maroon-cosmic feel without being distracting.
 *
 * Layers (in order):
 *  1. Animated multi-stop maroon→gold radial gradient (gradient-shift)
 *  2. Concentric mandala overlay (large, slow rotation)
 *  3. Drifting glow orbs (3 staggered)
 *  4. Twinkling gold "stars" (12 deterministic positions)
 *  5. Bottom fade so the section content can sit on top cleanly
 *
 * Usage: place absolutely as the first child of a relatively-positioned section.
 */
const STAR_POS = [
  [12, 18], [22, 64], [34, 28], [48, 78], [62, 22], [76, 58],
  [88, 38], [18, 86], [40, 14], [70, 84], [82, 12], [56, 50],
];

const MaroonAmbientBackdrop = ({ variant = 'hero', className = '' }) => {
  const heavy = variant === 'hero';

  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    >
      {/* Layer 1 — animated cosmic gradient */}
      <div
        className="absolute inset-0 animate-gradient-shift"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 15% 0%, rgba(115,17,30,0.18) 0%, transparent 45%), radial-gradient(ellipse at 85% 0%, rgba(212,175,55,0.16) 0%, transparent 45%), radial-gradient(ellipse at 50% 100%, rgba(74,9,18,0.10) 0%, transparent 55%)',
          backgroundSize: '200% 200%',
        }}
      />

      {/* Layer 2 — subtle static cosmic mandala accent (no rotation) */}
      {heavy && (
        <div
          className="absolute -top-24 -right-24 w-[640px] h-[640px] bg-no-repeat bg-center opacity-25"
          style={{
            backgroundImage: "url('/generated/cosmic-mandala.webp')",
            backgroundSize: 'contain',
            WebkitMaskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
            maskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
          }}
        />
      )}
      <div
        className="absolute -bottom-32 -left-32 w-[480px] h-[480px] bg-no-repeat bg-center opacity-20"
        style={{
          backgroundImage: "url('/generated/cosmic-mandala.webp')",
          backgroundSize: 'contain',
          WebkitMaskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
          maskImage: 'radial-gradient(circle at center, #000 40%, transparent 75%)',
        }}
      />

      {/* Layer 3 — drifting glow orbs */}
      <div
        className="absolute w-[420px] h-[420px] rounded-full blur-3xl animate-drift"
        style={{
          top: '10%',
          left: '5%',
          background: 'radial-gradient(circle, rgba(115,17,30,0.22) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute w-[360px] h-[360px] rounded-full blur-3xl animate-drift"
        style={{
          top: '40%',
          right: '8%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)',
          animationDelay: '4s',
          animationDuration: '20s',
        }}
      />
      <div
        className="absolute w-[300px] h-[300px] rounded-full blur-3xl animate-drift"
        style={{
          bottom: '8%',
          left: '40%',
          background: 'radial-gradient(circle, rgba(243,229,171,0.12) 0%, transparent 70%)',
          animationDelay: '8s',
          animationDuration: '22s',
        }}
      />

      {/* Layer 4 — twinkling gold stars */}
      {STAR_POS.map(([x, y], i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-brand-gold animate-twinkle"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            animationDelay: `${(i * 0.4) % 3}s`,
            boxShadow: '0 0 8px rgba(212,175,55,0.85)',
          }}
        />
      ))}
    </div>
  );
};

export default MaroonAmbientBackdrop;
